import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

/**
 * REST client for Patient Queue definitions (admin/configuration — QUEUE_SPECIFICATION.md §6.2).
 * Mirrors src/lib/api/admin-slots.js. Every call is employee-gated server-side and tenant-scoped
 * to the caller; the editor never sends tenantId.
 */

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function jsonHeaders(token) {
  return {
    ...createAuthHeaders(token),
    "Content-Type": "application/json",
  };
}

function requireQueueId(queueId) {
  const id = String(queueId || "").trim();
  if (!id) {
    throw new ApiError("queueId is required", 400);
  }
  return id;
}

/** GET /api/admin/queues — list this tenant's queue definitions. Returns an array. */
export async function fetchQueueDefinitions({ token } = {}) {
  const res = await apiRequest(`/api/admin/queues`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return Array.isArray(res?.items) ? res.items : [];
}

/** GET /api/admin/queues/{queueID} — read one queue definition. */
export async function fetchQueueDefinition({ token, queueId } = {}) {
  const id = requireQueueId(queueId);
  return apiRequest(`/api/admin/queues/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/** POST /api/admin/queues — create a queue definition. `body` is a normalized queue draft. */
export async function createQueueDefinition({ token, body } = {}) {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/queues`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/** PUT /api/admin/queues/{queueID} — replace the editable fields of a queue definition. */
export async function updateQueueDefinition({ token, queueId, body } = {}) {
  const id = requireQueueId(queueId);
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/queues/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/** DELETE /api/admin/queues/{queueID} — soft-delete (returns 204 → null). */
export async function deleteQueueDefinition({ token, queueId } = {}) {
  const id = requireQueueId(queueId);
  return apiRequest(`/api/admin/queues/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

/**
 * GET /api/queue/checkins — flat per-entry check-in state for a date across all
 * of the caller-tenant's clinics (Patient Queue runtime). The calendar's
 * check-in indicator joins these onto appointment cards by `appointmentId`
 * (manual adds carry only `patientId`).
 * @returns {Promise<{ date: string, items: Array<{
 *  entryId: string, appointmentId?: string, patientId?: string,
 *  clinicId?: string, lifecycle: string, checkedInAt?: string,
 *  visitActive?: boolean,
 * }>, count: number }>}
 */
export async function fetchQueueCheckins({ token, date } = {}) {
  const query = new URLSearchParams();
  const normalizedDate = String(date || "").trim();
  if (normalizedDate) {
    query.set("date", normalizedDate);
  }
  const path = query.toString() ? `/api/queue/checkins?${query.toString()}` : "/api/queue/checkins";
  const payload = await apiRequest(path, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  const items = Array.isArray(payload?.items)
    ? payload.items.map((item) => ({
      entryId: String(item?.entryId || "").trim(),
      appointmentId: String(item?.appointmentId || "").trim(),
      patientId: String(item?.patientId || "").trim(),
      clinicId: String(item?.clinicId || "").trim(),
      lifecycle: String(item?.lifecycle || "").trim(),
      checkedInAt: String(item?.checkedInAt || "").trim(),
      visitActive: Boolean(item?.visitActive),
    }))
    : [];
  return {
    date: String(payload?.date || normalizedDate).trim(),
    items,
    // Clinic (tenant) ids with a live queue instance on this date — the
    // calendar only derives "not arrived" marks for covered clinics.
    clinics: Array.isArray(payload?.clinics)
      ? payload.clinics.map((id) => String(id || "").trim()).filter(Boolean)
      : [],
    count: Number(payload?.count) || items.length,
  };
}
